<template>
  <div>
    <section class="content-group -bg-01 -center -no-spacer -vs-medium -column">
      <div class="group -flex -between -narrow -vs-small -vs-bottom -vs-collapse-bottom">
        <h1 id="spa-title" class="heading -primary">
          <span class="pre-heading">Personalize Your</span>
          Meal Plans
        </h1>
        <div v-if="from" class="spa-heading-action">
          <a :href="from">
            {{ fromName ? `Back to ${fromName}` : '' }}
          </a>
        </div>
      </div>
      <div class="group -narrow -vs-small -vs-collapse-top">
        <p>
          These options allow you to personalize Meal Plans to match your preferred approach to
          food, your daily energy needs, and any goals you may have. As your preferences or goals
          change over time, you can update your information by visiting these options again.
        </p>
      </div>

      <!-- Dietary Approach -->
      <router-link
        to="/my/settings/energy/dietary"
        class="preference-card group -flex -between -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double"
        :class="{
          '-mindful': dietaryMethod === 'mindful',
          '-estimates': dietaryMethod === 'estimate',
        }"
      >
        <div v-if="dietaryMethod === 'mindful'" class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Dietary Approach</span>
            Mindful Eating
          </h2>
          <p>
            I want to focus on how I feel, enjoy healthy foods when I’m hungry, and be mindful about
            when I’m full. I do not want to see calories and macronutrients in meal plans.
          </p>
        </div>
        <div v-else-if="dietaryMethod === 'estimate'" class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Dietary Approach</span>
            Nutrition Estimates
          </h2>
          <p>
            I want to use data to help me estimate and track my nutrition and activity balance. I
            want to see calories and macronutrients in meal plans.
          </p>
        </div>
        <div v-else class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Step 1</span>
            Dietary Approach
          </h2>
          <p>Select your preference for how you approach food and nutrition.</p>
        </div>
        <div class="selection-icon">
          <i v-if="dietaryMethod" class="material-symbols-outlined">check_circle</i>
          <i v-else class="material-symbols-outlined -rotate-neg-90">expand_circle_down</i>
        </div>
      </router-link>
      <!-- End Dietary Approach -->

      <!-- Daily Energy -->
      <router-link
        to="/my/settings/energy/calorie"
        class="preference-card group -flex -between -stretch -narrow -vs-medium -vs-center -hs-medium -bg-08 -only-rounder -stack-space-double"
        :class="{
          '-range': energyMethod === 'range',
          '-goal': energyMethod === 'goal',
          '-calculator': energyMethod === 'calculator',
        }"
      >
        <div v-if="energyMethod === 'range'" class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Daily Energy Needs</span>
            {{ calorieType.name }}
          </h2>
          <p>
            I will use a {{ calorieType.name }} General Range and adjust what I need based on how I
            feel.
          </p>
        </div>
        <div v-else-if="energyMethod === 'goal'" class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Daily Energy Needs</span>
            {{ calorieType.name }}
          </h2>
          <p>
            I will use a Calorie Goal of approximately
            {{ calorieType.calMin }} - {{ calorieType.calMax }} per day and adjust what I need based
            on my desired results.
          </p>
        </div>
        <div v-else-if="energyMethod === 'calculator'" class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Daily Energy Needs</span>
            {{ calorieType.name }}
          </h2>
          <p>
            I will use a Calorie Calculator estimate of approximately {{ calorieType.calMin }} -
            {{ calorieType.calMax }} per day and adjust what I need based on my desired results.
          </p>
        </div>
        <div v-else class="selection-text">
          <h2 class="heading -secondary">
            <span class="pre-heading -caps">Step 2</span>
            Daily Energy Needs
          </h2>
          <p>Adjust how much energy you need each day to feel your best.</p>
        </div>
        <div class="selection-icon">
          <i v-if="energyMethod" class="material-symbols-outlined">check_circle</i>
          <i v-else class="material-symbols-outlined -rotate-neg-90">expand_circle_down</i>
        </div>
      </router-link>
      <!-- End Daily Energy -->

      <!-- Clear Selections -->
      <div class="spa-action">
        <div v-if="from">
          <a :href="from" class="btn -main">
            {{ fromName ? `Back to ${fromName}` : '' }}
          </a>
        </div>
        <div v-show="dietaryMethod || energyMethod">
          <submit-button
            :submitting="deleting"
            label="Clear My Selections"
            submitting-label="Processing…"
            classes="-link"
            @submit-action="openClearModal"
          />
        </div>
      </div>

      <!-- End Clear Selections -->
    </section>

    <more-info-section title="How does this personalize my meal plans?">
      <p>
        Selecting your Dietary Approach lets you choose between mindful eating or using calories and
        macronutrients in your meal plans. Both approaches provide the same meals and allow you to
        adjust your daily energy intake as you go, but the mindful approach lets you focus on what
        your body is telling you, versus tracking and calculating calories and macros to stay on
        track. If you have a history of eating disorders, the mindful approach is a good option to
        avoid triggers related to tracking numbers.
      </p>
      <p>
        Selecting your Daily Energy Needs gives you the flexibility to adjust how much food you need
        each day to feel your best or reach any dietary goals you’re trying to achieve. You can
        adjust your settings as you go to account for a relaxing weekend off, or if you’re crushing
        those workout completes. The plans are not meant to be rigid, so feel free to adjust your
        portions and snacks depending on your hunger level too.
      </p>
    </more-info-section>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useModalsStore } from '@/stores/modals'
import SubmitButton from '@/components/buttons/submit-button.vue'
import MoreInfoSection from './more-info-section.vue'

/**
 * Step 1 - Intro
 *
 * Handles the initial jumping off points for dietary method and energy method
 * calorie selection.
 *
 * Shows the value for users dietary_method and energy_method.
 */
export default defineComponent({
  name: 'IntroStep',
  components: {
    SubmitButton,
    MoreInfoSection,
  },
  props: {
    dietaryMethod: {
      type: String,
      default: '',
    },
    energyMethod: {
      type: String,
      default: '',
    },

    // single entry from calorieType
    calorieType: {
      type: Object,
      default: () => null,
    },

    // powers back link
    from: {
      type: String,
      default: '',
    },
    fromName: {
      type: String,
      default: '',
    },

    // loading state from parent
    deleting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['delete-action'],
  data() {
    return {}
  },

  computed: {
    ...mapState(useModalsStore, ['simpleModal']),

    modalId() {
      return 'energy-clear-modal'
    },
  },

  watch: {
    'simpleModal.action'(newAction, prevAction) {
      if (
        this.simpleModal.id === this.modalId &&
        newAction !== prevAction &&
        newAction === 'confirm'
      ) {
        this.$emit('delete-action')
      }
    },
  },

  methods: {
    ...mapActions(useModalsStore, ['openSimpleModal']),

    openClearModal() {
      this.openSimpleModal({
        type: 'CONFIRM',
        id: this.modalId,
        props: {
          heading: 'Clear Energy Settings',
          subheading:
            'Clear your dietary and energy selections to return meal plans to their default settings.',
          buttonText: 'Clear',
        },
      })
    },
  },
})
</script>
